import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { END_POINTS_PATH } from '../../../routes/paths';
import { EntityServices } from '../../../Services/EntityServices';
import { settingsKeys } from './queryKeys';
import ShowSnackbar from '../../../Services/snackbar';
import { IPWhitelist } from '../../../types/ipWhitelist';
import { HOST_API_URL_V2 } from '../../../config';

const svc = new EntityServices<IPWhitelist>();

export const useIPWhitelist = (apiKey?: string) => {
  return useQuery<IPWhitelist[]>({
    queryKey: settingsKeys.ipWhitelist(apiKey),
    enabled: !!apiKey,
    queryFn: async () => {
      const res = await svc.GetAllAsync(`${HOST_API_URL_V2}${END_POINTS_PATH.IPWhitelist}/byApiKey/${apiKey}`, undefined, undefined, undefined, true);
      if (res?.statusCode === 200) return res.data || [];
      throw new Error(res?.message || 'Failed to load IP whitelist');
    }
  });
};

export const useCreateOrUpdateIP = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: IPWhitelist): Promise<IPWhitelist> => {
      if (payload.id) {
        const res = await svc.UpdateAsync(`${HOST_API_URL_V2}${END_POINTS_PATH.IPWhitelist}`, payload, payload.id, undefined, true);
        if (res?.statusCode === 200) return res.data;
        throw new Error(res?.message || 'Update failed');
      } else {
        const res = await svc.AddAsync(`${HOST_API_URL_V2}${END_POINTS_PATH.IPWhitelist}`, payload, undefined, true);
        if (res?.statusCode === 201 || res?.statusCode === 200) return res.data;
        throw new Error(res?.message || 'Create failed');
      }
    },
    onSuccess: (_data: IPWhitelist, vars: IPWhitelist) => {
      ShowSnackbar('Saved', 'primary');
      qc.invalidateQueries({ queryKey: settingsKeys.ipWhitelist(vars.apiKey) });
    },
    onError: (err: any) => ShowSnackbar(err.message || 'Save failed', 'danger')
  });
};

export const useDeleteIP = (apiKey?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const ok = await svc.DeleteAsync(`${HOST_API_URL_V2}${END_POINTS_PATH.IPWhitelist}`, id, undefined, true);
      if (!ok) throw new Error('Delete failed');
      return id;
    },
    onSuccess: () => {
      ShowSnackbar('Deleted', 'primary');
      qc.invalidateQueries({ queryKey: settingsKeys.ipWhitelist(apiKey) });
    },
    onError: (err: any) => ShowSnackbar(err.message || 'Delete failed', 'danger')
  });
};

export const useToggleActiveIP = (apiKey?: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }): Promise<{ id: string; active: boolean }> => {
      const res = await svc.AddFromAnotherModelAsync<any>(`${HOST_API_URL_V2}${END_POINTS_PATH.IPWhitelist}/${id}/activate?active=${active}`, {}, undefined, true);
      if (res?.statusCode === 200) return { id, active };
      throw new Error(res?.message || 'Update failed');
    },
    onSuccess: () => {
      ShowSnackbar('Updated', 'primary');
      qc.invalidateQueries({ queryKey: settingsKeys.ipWhitelist(apiKey) });
    },
    onError: (err: any) => ShowSnackbar(err.message || 'Update failed', 'danger')
  });
};
